import {IconButton} from '@primer/react'
import type {IconButtonProps} from '@primer/react'
import {SquareCircleIcon} from '@primer/octicons-react'
import type {Session} from '../../types/session'
import {SessionState} from '../../types/session'
import {useStopSession} from '../../hooks/use-stop-session'
import {useIntentDetection} from '../../hooks/useIntentDetection'
import {sendEvent} from '@github-ui/hydro-analytics'

// waiting_for_user is intentionally excluded: in our paused-session model the
// runtime is not running while waiting for the user, so there is nothing to
// stop. Resume runs as a separate session; the OLD session is conceptually
// done. (When a pending prompt is present, PromptInput replaces the chat input
// entirely and the Stop button doesn't render either way.)
const STOPPABLE_STATES: string[] = [SessionState.InProgress, SessionState.Idle]

interface TaskStopButtonProps {
  session: Session
  nwo: string | undefined
  currentUserCanPush: boolean
  tooltipDirection?: IconButtonProps['tooltipDirection']
}

/**
 * Stop button for the chat input area. Accepts a session directly
 * instead of relying on SessionContext (which wraps individual sessions
 * in the logs), so it can be rendered at the task/input level.
 */
export function TaskStopButton({session, nwo, currentUserCanPush, tooltipDirection}: TaskStopButtonProps) {
  const {isChatSession} = useIntentDetection(session)
  const {isSuccess, isPending, mutate} = useStopSession({nwo, session})
  const sessionStoppable = STOPPABLE_STATES.includes(session.state) && !isSuccess

  if (isChatSession || !nwo || !currentUserCanPush || !sessionStoppable) return null

  return (
    <IconButton
      icon={SquareCircleIcon}
      variant="invisible"
      className="fgColor-danger"
      size="medium"
      aria-label="Stop session"
      tooltipDirection={tooltipDirection}
      disabled={isPending}
      onClick={() => {
        if (!isPending) {
          mutate()
          sendEvent('agent_sessions.task_view', {
            target: 'STOP_SESSION_BUTTON',
            session_id: session.id,
            task_id: session.resource_global_id,
          })
        }
      }}
      loading={isPending}
    />
  )
}
